import i18n from "i18next";
import { Alert, Box } from "@mui/material";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { PropertyModelInputField } from "src/components/views/erp/configurator/configuratorForm/propertyModel/PropertyModelInputField.tsx";
import {
	PartConfigurationSchemaProperty,
	PartConfigurationSchemaValidationIssue,
	PartConfigurationValue,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import { getShownPropertyModelIssues } from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputFieldUtils.ts";
import {
	getUnevaluatedChildIndexes,
	isSubConfigurationProperty,
	PropertyInputValues,
	PropertyModelNode,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

export interface PropertyModelConfigurationNodeFormProps {
	node: PropertyModelNode;
	/** The properties of the node's model, in layout order. */
	schemaProperties: PartConfigurationSchemaProperty[];
	issues: PartConfigurationSchemaValidationIssue[];
	inputValues: PropertyInputValues;
	readonly: boolean;
	/** The properties of the node that the user has edited, whose missing values are shown. */
	editedPropertyNames: ReadonlySet<string>;
	/** Whether the user has tried to save, so that missing values are shown before they are edited. */
	showAllMissingValueIssues: boolean;
	onInputChanged: (propertyName: string, value: PartConfigurationValue | undefined) => Promise<unknown>;
	/** Removes the given children of [propertyName] at [indexes]. */
	removeChildren: (propertyName: string, indexes: number[]) => Promise<unknown>;
}

/** The inputs of one configuration of a property-model tree, in the order of its properties. */
export function PropertyModelConfigurationNodeForm({
	node,
	schemaProperties,
	issues,
	inputValues,
	readonly,
	editedPropertyNames,
	showAllMissingValueIssues,
	onInputChanged,
	removeChildren,
}: PropertyModelConfigurationNodeFormProps) {
	const evaluationsByName = new Map(node.properties.map((property) => [property.name, property]));
	// Calculated values are not shown, like the script-calculated properties of the component model.
	const inputProperties = schemaProperties.filter(
		(property) => property.kind === "INPUT" && evaluationsByName.get(property.name)?.applicable === true,
	);
	const fieldProperties = inputProperties.filter((property) => !isSubConfigurationProperty(property));
	const subConfigurationProperties = inputProperties.filter((property) => isSubConfigurationProperty(property));
	// Issues of properties without a field, such as sub-configurations and calculated values, are shown above the
	// fields.
	const fieldNames = new Set(fieldProperties.map((property) => property.name));
	const shownIssues = getShownPropertyModelIssues({ issues, editedPropertyNames, showAllMissingValueIssues });
	const generalIssues = shownIssues.filter(
		(issue) => issue.propertyName == null || !fieldNames.has(issue.propertyName),
	);

	return (
		<VerticalBox
			// The fields keep their height, with helper texts below them, and the form scrolls instead.
			sx={{ flex: 1, overflow: "auto", padding: 2, gap: 2, "& > *": { flexShrink: 0 } }}
		>
			{generalIssues.map((issue, index) => (
				<Alert key={index} severity={"error"}>
					{issue.message}
				</Alert>
			))}
			<Box
				sx={{
					display: "grid",
					// Up to three columns of equal width, one on a narrow screen.
					gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
					maxWidth: 1200,
					alignItems: "start",
					columnGap: 2,
					rowGap: 1.5,
				}}
			>
				{fieldProperties.map((property) => (
					<Box key={property.name} sx={{ display: "flex", minWidth: 0 }}>
						<PropertyModelInputField
							property={property}
							evaluation={evaluationsByName.get(property.name)!}
							issueMessages={shownIssues
								.filter((issue) => issue.propertyName === property.name)
								.map((issue) => issue.message)}
							disabled={readonly}
							onChange={(value) => onInputChanged(property.name, value)}
						/>
					</Box>
				))}
			</Box>
			{!readonly &&
				subConfigurationProperties.map((property) => {
					const unevaluatedIndexes = getUnevaluatedChildIndexes(inputValues, node.properties, property.name);
					if (unevaluatedIndexes.length === 0) return null;
					return (
						<AsyncButton
							key={property.name}
							icon={faTrash}
							variant={"outlined"}
							color={"error"}
							label={`${i18n.t("remove_unevaluated_sub_configurations")}: ${property.title || property.name}`}
							onClick={() => removeChildren(property.name, unevaluatedIndexes)}
							sx={{ alignSelf: "flex-start" }}
						/>
					);
				})}
		</VerticalBox>
	);
}
